// Photo upload field with crop / preview / replace / delete.
// Output is always a JPEG data URL (normalizes webp/png for pdf-lib).
import React, { useCallback, useRef, useState } from "react";
import Cropper from "react-easy-crop";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ImagePlus, Crop, Trash2, RefreshCw } from "lucide-react";

function getCroppedDataUrl(src, cropPixels, maxW = 600) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      const scale = Math.min(1, maxW / cropPixels.width);
      canvas.width = Math.round(cropPixels.width * scale);
      canvas.height = Math.round(cropPixels.height * scale);
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, cropPixels.x, cropPixels.y, cropPixels.width, cropPixels.height, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL("image/jpeg", 0.85));
    };
    img.onerror = reject;
    img.src = src;
  });
}

export default function PhotoField({ value, onChange, label = "Photo", aspect = 4 / 5, testid }) {
  const inputRef = useRef(null);
  const [rawSrc, setRawSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedArea, setCroppedArea] = useState(null);

  const onFile = (file) => {
    if (!file) return;
    if (!/^image\/(jpe?g|png|webp)$/i.test(file.type)) { toast.error("Use JPG, PNG or WebP"); return; }
    if (file.size > 4_000_000) { toast.error("Image must be under 4 MB"); return; }
    const reader = new FileReader();
    reader.onload = () => setRawSrc(reader.result);
    reader.readAsDataURL(file);
  };

  const onCropComplete = useCallback((_, pixels) => setCroppedArea(pixels), []);

  const applyCrop = async () => {
    try {
      const url = await getCroppedDataUrl(rawSrc, croppedArea);
      onChange(url);
      setRawSrc(null);
    } catch {
      toast.error("Crop failed");
    }
  };

  return (
    <div data-testid={testid}>
      {value ? (
        <div className="flex items-center gap-3">
          <img src={value} alt={label} className="h-16 w-14 object-cover rounded border border-border" />
          <div className="flex gap-1.5">
            <Button type="button" variant="outline" size="sm" className="h-8 text-xs" onClick={() => inputRef.current?.click()} data-testid={`${testid}-replace`}>
              <RefreshCw className="w-3.5 h-3.5 mr-1" /> Replace
            </Button>
            <Button type="button" variant="ghost" size="sm" className="h-8 text-xs text-red-600" onClick={() => onChange("")} data-testid={`${testid}-delete`}>
              <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
            </Button>
          </div>
        </div>
      ) : (
        <Button type="button" variant="outline" size="sm" className="h-9 border-dashed" onClick={() => inputRef.current?.click()} data-testid={`${testid}-upload`}>
          <ImagePlus className="w-4 h-4 mr-1.5" /> Upload {label}
        </Button>
      )}
      <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
        onChange={e => { onFile(e.target.files?.[0]); e.target.value = ""; }} />

      <Dialog open={!!rawSrc} onOpenChange={v => !v && setRawSrc(null)}>
        <DialogContent className="max-w-md" data-testid={`${testid}-crop-dialog`}>
          <DialogHeader><DialogTitle>Crop {label}</DialogTitle></DialogHeader>
          <div className="relative w-full h-72 bg-black/80 rounded overflow-hidden">
            {rawSrc && (
              <Cropper image={rawSrc} crop={crop} zoom={zoom} aspect={aspect}
                onCropChange={setCrop} onZoomChange={setZoom} onCropComplete={onCropComplete} />
            )}
          </div>
          <input type="range" min={1} max={3} step={0.05} value={zoom} onChange={e => setZoom(+e.target.value)} className="w-full" data-testid={`${testid}-zoom`} />
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setRawSrc(null)}>Cancel</Button>
            <Button onClick={applyCrop} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid={`${testid}-apply`}>
              <Crop className="w-4 h-4 mr-1.5" /> Apply
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
